import React, { useState } from 'react';

import useGlobalState from '../../GlobalStateContext';
import Search from '../../elements/Search';
import Card from '../../elements/common/Card';
import TableLoader from '../../elements/common/TableLoader';
import Paginator from '../../elements/paginator';
import { PERMISSION } from '../../helpers/Permissions';
import useAutoPaginator, { FetchPaginatorParams } from '../../hooks/useAutoPaginator';
import useFetchState from '../../hooks/useFetchState';

import DepositsTableDefault from './DepositsTableDefault';
import DepositsTableShort from './DepositsTableShort';
import ManualDepositModal from './ManualDepositModal';

import CRMApi from 'Api/CRM/CRMApi';
import GetDepositResponse from 'Api/CRM/Responses/DepositResponse';
import { DepositStatus } from 'Api/CRM/Responses/Enums/DepositStatus';
import { SelectOptions } from 'components/common/SelectInput';
import DownloadButton from 'crm/elements/DownloadButton';
import FilterByColumn, { Filter } from 'crm/elements/common/FilterByColumn';
import { createOptionsFromEnum } from 'crm/helpers/formUtils';

const depositStatuses = createOptionsFromEnum(DepositStatus);

type Props = {
    short?: boolean;
    addManualButton?: boolean;
} & ({ source?: 'deposits'; relatedId?: never } | { source: 'user'; relatedId: number });

function DepositsList({ source = 'deposits', relatedId, short = false, addManualButton = false }: Readonly<Props>) {
    const isMainSource = source === 'deposits';
    const url = isMainSource ? 'deposit' : `user/${relatedId}/deposits`;
    const { loggedAdmin } = useGlobalState();
    const [queryParams, setQueryParams] = useState<FetchPaginatorParams>();

    const { items, updateItems, isLoading, auto } = useAutoPaginator<GetDepositResponse>(async (page, params) => {
        setQueryParams(params);

        return (await CRMApi.get(url, { params: { page, ...params } })).data;
    });

    const [manualDepositEnabled] = useFetchState(false, (setState) =>
        CRMApi.deposits.isManualDepositEnabled().then(setState)
    );
    const [methodNames] = useFetchState({} as SelectOptions, (setState) =>
        CRMApi.paymentMethods.getNames().then(setState)
    );

    const tableHeader = (
        <>
            <span className="mr-3">{isMainSource ? 'All deposits' : 'Deposits'}</span>
            {isMainSource && (
                <DownloadButton
                    action="deposits-as-csv"
                    title="Export to CSV"
                    permissions={[PERMISSION.DOWNLOAD_DEPOSITS]}
                    defaultFilename="deposits.csv"
                    params={queryParams}
                />
            )}
            {addManualButton && loggedAdmin.hasPermission(PERMISSION.CREATE_MANUAL_DEPOSIT) && manualDepositEnabled && (
                <ManualDepositModal userId={relatedId} onSuccess={updateItems} />
            )}
        </>
    );

    return (
        <Card title={tableHeader}>
            <Search {...auto.search} />
            <FilterByColumn {...auto.filter}>
                {!short && [
                    <Filter
                        key="payment_method"
                        column="payment_method_id"
                        type="select"
                        label="Payment method"
                        options={methodNames}
                    />,
                    <Filter key="status" column="status" type="select" options={depositStatuses} />,
                    <Filter key="date_confirmed" column="date_confirmed" label="Date confirmed" type="dateRange" />,
                    <Filter key="created_at" column="deposits.created_at" label="Created at" type="dateRange" />,
                ]}
            </FilterByColumn>
            <TableLoader isLoading={isLoading}>
                {short ? (
                    <DepositsTableShort deposits={items} />
                ) : (
                    <DepositsTableDefault deposits={items} sortProps={auto.sort} />
                )}
                <Paginator {...auto.paginator} />
            </TableLoader>
        </Card>
    );
}

export default DepositsList;
